import { useEffect, useState } from "react";
import { ContactRow } from "../../api/relationship.type";
import { useGroupChatContext } from "../../hooks/GroupChatContextWrapper";
import { useLoadContacts } from "../../hooks/useLoadContacts";
import { SubmitButton } from "../shared/buttons/submit-button";
import Input from "../shared/input";
import { Modal } from "../shared/modal";
import Typography from "../shared/typography";

interface Props {
  groupId: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function EditGroupChatModal({
  groupId,
  isOpen,
  onClose,
}: Props) {
  const {
    selectedGroupChat,
    selectGroupChat,
    groupChatMembers,
    updateGroupChatName,
    addMemberToGroupChat,
    removeMemberFromGroupChat,
  } = useGroupChatContext();

  const { contacts } = useLoadContacts();
  const [name, setName] = useState("");

  useEffect(() => {
    if (isOpen) {
      selectGroupChat(groupId);
    }
  }, [isOpen, groupId, selectGroupChat]);

  useEffect(() => {
    if (selectedGroupChat) {
      setName(selectedGroupChat.name);
    }
  }, [selectedGroupChat]);

  const availableContacts = contacts.filter(
    (c) => !groupChatMembers.some((m) => m.user_id === c.related_user_id)
  );

  const handleAdd = (contact: ContactRow) => {
    addMemberToGroupChat(groupId, contact);
  };

  const handleRemove = (memberId: string) => {
    removeMemberFromGroupChat(memberId);
  };

  const handleSaveName = () => {
    if (name.trim() && selectedGroupChat && name !== selectedGroupChat.name) {
      updateGroupChatName(groupId, name.trim());
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Group Chat">
      <div className="space-y-4">
        <div className="flex items-end gap-2">
          <Input
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            label="Group name"
          />
          <SubmitButton size="sm" onClick={handleSaveName}>
            Save
          </SubmitButton>
        </div>

        <div>
          <Typography variant="subtitle1" className="mb-2 text-terracotta">
            Members ({groupChatMembers.length})
          </Typography>
          <ul className="space-y-1">
            {groupChatMembers.map((m) => (
              <li
                key={m.id}
                className="flex items-center justify-between bg-cream-100 p-2 rounded"
              >
                <span>
                  {m.user.first_name} {m.user.last_name}
                </span>
                <SubmitButton
                  color="cream"
                  size="sm"
                  iconOnly
                  onClick={() => handleRemove(m.id)}
                >
                  x
                </SubmitButton>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <Typography variant="subtitle1" className="mb-2 text-terracotta">
            Add Members
          </Typography>
          <ul className="space-y-1 max-h-40 overflow-auto">
            {availableContacts.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between bg-cream-50 p-2 rounded"
              >
                <span>
                  {c.related_user.first_name} {c.related_user.last_name}
                </span>
                <SubmitButton
                  color="cream"
                  size="sm"
                  onClick={() => handleAdd(c)}
                >
                  Add
                </SubmitButton>
              </li>
            ))}
            {availableContacts.length === 0 && (
              <Typography variant="body2" className="text-brown-600">
                No contacts to add
              </Typography>
            )}
          </ul>
        </div>
      </div>
    </Modal>
  );
}
